import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from 'react';

type SnackbarState = {
  message: string;
  open: boolean;
  autoHideDuration?: number;
};

type SnackbarAction =
  | { type: 'SHOW_SNACKBAR'; payload: Partial<SnackbarState> }
  | { type: 'HIDE_SNACKBAR' };

type SnackbarContextType = {
  snackbar: SnackbarState;
  showSnackbar: (payload: {
    message: string;
    autoHideDuration?: number;
  }) => void;
  hideSnackbar: () => void;
};

const initialState: SnackbarState = {
  message: '',
  open: false,
  autoHideDuration: 4000,
};

const snackbarReducer = (
  state: SnackbarState,
  action: SnackbarAction
): SnackbarState => {
  switch (action.type) {
    case 'SHOW_SNACKBAR':
      return {
        ...state,
        open: true,
        autoHideDuration: 4000,
        ...action.payload,
      };
    case 'HIDE_SNACKBAR':
      return {
        ...state,
        open: false,
      };
    default:
      return state;
  }
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [snackbar, dispatch] = useReducer(snackbarReducer, initialState);

  const showSnackbar = useCallback(
    (payload: { message: string; autoHideDuration?: number }) => {
      dispatch({ type: 'SHOW_SNACKBAR', payload });
    },
    []
  );

  const hideSnackbar = useCallback(() => {
    dispatch({ type: 'HIDE_SNACKBAR' });
  }, []);

  return (
    <SnackbarContext.Provider value={{ snackbar, showSnackbar, hideSnackbar }}>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
